import axios from 'axios';
import config from '../config';

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Attaches token request and silent refresh response interceptors to an Axios instance.
 * @param {import('axios').AxiosInstance} instance 
 */
export const setupInterceptors = (instance) => {
  // Request Interceptor: Attach access token
  instance.interceptors.request.use(
    (reqConfig) => {
      let token = localStorage.getItem('token') || localStorage.getItem('authToken');
      if (token) {
        token = token.replace(/['"]/g, '').trim();
        reqConfig.headers.Authorization = `Bearer ${token}`;
      }
      return reqConfig;
    },
    (error) => Promise.reject(error)
  );

  // Response Interceptor: Silent refresh on 401 Unauthorized
  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (!originalRequest) {
        return Promise.reject(error);
      }

      const requestUrl = originalRequest.url || '';
      const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/refresh-token');

      // Intercept 401 Unauthorized errors (excluding login/refresh attempts and already-retried requests)
      if (error.response?.status === 401 && !originalRequest._retry && !isAuthRequest) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((newToken) => {
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return instance(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          isRefreshing = false;
          console.warn('No refresh token available. Logging out.');
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userInfo');
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
          return Promise.reject(error);
        }

        try {
          const apiBaseUrl = config.API_BASE_URL || 'https://slateblue-mallard-430836.hostingersite.com';
          
          // Use raw unintercepted axios call to refresh token
          const { data } = await axios.post(
            `${apiBaseUrl}/api/auth/refresh-token`,
            { refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
          );

          const newToken = data.token || data.accessToken;
          const newRefreshToken = data.refreshToken || refreshToken;

          if (newToken) {
            localStorage.setItem('token', newToken);
            localStorage.setItem('authToken', newToken);
          }
          if (newRefreshToken) {
            localStorage.setItem('refreshToken', newRefreshToken);
          }

          // Update Authorization header for original request and future calls
          instance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;

          processQueue(null, newToken);
          isRefreshing = false;

          return instance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError, null);
          isRefreshing = false;

          console.error('Refresh token expired or invalid. Logging out.', refreshError);
          localStorage.removeItem('token');
          localStorage.removeItem('authToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userInfo');

          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};

// Automatically attach interceptor to global default axios instance
setupInterceptors(axios);

export default setupInterceptors;
